import React, { ReactElement, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import ReactDiffViewer from 'react-diff-viewer'
import moment from 'moment'
import i18n from '@/i18n'
import { IAppState } from '@/store/reducers'
import { useSelector } from 'react-redux'


interface IChangeField {
    after: any
    before: any
    field: string
}

interface IProps {
    dataObj: {
   changeFields: string
    }
    record: any
}

function DiffComp (props: IProps): ReactElement {
    const { t } = useTranslation()
    const workgroupList = useSelector(
        (state: IAppState) => state.tableEditData.workgroupList
    )
    const { dataObj, record } = props
    const [diffResult, setDiffResult] = useState<{oldValue: string, newValue: string}>({
        oldValue: '',
        newValue: ''
    })

    // 封装拼接字符串的函数
    const joinTranslatedStrings = (...translations: string[]) => {
        return translations.join(i18n.language !== 'zh' ? ' ' : '')
    }

    // 字段映射表
    const fieldLabelMap = {
        dueDate: t('dueDate'),
        reviewDate: joinTranslatedStrings(t('reviewMeeting'), t('date')),
        description: joinTranslatedStrings(t('technicalPlan'), t('proposalSummary')),
        proposalName: t('proposalName'),
        reviewType: t('proposalStage'),
        workGroupName: t('Work Group'),
        workGroupId: t('Work Group'),
        workGroup:t('Work Group'),
        version: t('version'),
        isCommunicate: t('isCommunicate'), 
        isPlReview: t('selectTech'),
        reviewResult: t('reviewResult'),
        technologyLeadership: t('technologicalLeadership'),
        technologyContribution: t('technicalContribution'),
        coreTechnology: t('techType'),
        techLevel: t('techLevel'),
        techScope: t('technicalApplicationRange'),
        techPerformance: t('technicalPerformanceIndicators'),
        standardOutput: t('standardOutput'),
        standardName: t('standardName'),
        standardNumber: t('standardNumber'),
        techSource:t('technicalOrigin'),
        standardLink: t('standardLink'),
        parentTech:t('higherLevelTechnicalPoints'),
        subsidiary: t('subsidiary'),
        roadMap: t('roadMap'),
        firstUseDate: t('estimatedFirstApplicationTime'),
        productFunction: joinTranslatedStrings(t('productFeatures'), '(', t('USP'), ')'),
        technologyCertification: t('technologyCertification'),
        tmtReviewDueDate: t('feedDueDate'),
        reviewDateHours: joinTranslatedStrings(t('reviewMeeting'), t('time')),
        fileIds: t('attach'),
        dutyBuss: t('developDutySubject'),
        costBuss:record.reviewType !== 4
            ? t('developCostAttribute')
            : t('proposalAttributionBusinessModule')
    }

    // 处理日期格式化
    const formatDate = (timestamp: number) => {
        return moment(timestamp).format('YYYY-MM-DD HH:mm:ss')
    }

    // 处理 description 字段 - 只处理去除HTML标签
    const parseDescription = (jsonStr: string) => {
        try {
            // 如果是字符串且包含 HTML 标签，直接去除标签
            if (typeof jsonStr === 'string') {
                return jsonStr
                    .replace(/<[^>]*>/g, '')     // 移除所有 HTML 标签
                    .replace(/&nbsp;/g, ' ')     // 替换 HTML 实体
                    .replace(/&lt;/g, '<')       // 替换其他常见实体
                    .replace(/&gt;/g, '>')
                    .replace(/&amp;/g, '&')
                    .replace(/&quot;/g, '"')
                    .replace(/&#39;/g, "'")
                    .replace(/\s+/g, ' ')        // 合并多个空格
                    .trim()                      // 去除首尾空格
            }
            
            return jsonStr || ''
        } catch (e) {
            return jsonStr || ''
        }
    }

    // 处理单个字段的变更
    const processField = (field: IChangeField) => {
        const { after, before, field: fieldName } = field
        
        // 处理不同类型的字段
        let beforeValue = before
        let afterValue = after
        
        if (fieldName.includes('Date')) {
            beforeValue = formatDate(before)
            afterValue = formatDate(after)
        } else if (fieldName === 'description') {
            const beforeStr = JSON.parse(before)
            const afterStr = JSON.parse(after)
            beforeValue = parseDescription(beforeStr.str)
            afterValue = parseDescription(afterStr.str)
        } else if (fieldName === 'technologyLeadership') {
            // 技术引领性枚举转换
            const leadershipEnum = {
                1: t('leadership'),
                2: t('equalize'),
                3: t('trail'),
                4: t('homeleadership')
            }
            beforeValue = leadershipEnum[before as keyof typeof leadershipEnum] || before
            afterValue = leadershipEnum[after as keyof typeof leadershipEnum] || after
        } else if (fieldName === 'technologyContribution') {
            // 技术贡献性枚举转换 - 根据 technicalContributionArr
            const contributionEnum = {
                1: t('innovationSellingPoint'),
                2: t('keySellingPoint'),
                3: t('secondarySellingPoints'),
                4: t('infrastructure')
            }
            beforeValue = contributionEnum[before as keyof typeof contributionEnum] || before
            afterValue = contributionEnum[after as keyof typeof contributionEnum] || after
        } else if (fieldName === 'coreTechnology') {
            // 技术类型枚举转换
            const techTypeEnum = {
                1: joinTranslatedStrings(t('core'), t('technology')),
                2: joinTranslatedStrings(t('key'), t('technology')),
                3: t('basicTechnology'),
                4: t('reserveTechnology')
            }
            beforeValue = techTypeEnum[before as keyof typeof techTypeEnum] || before
            afterValue = techTypeEnum[after as keyof typeof techTypeEnum] || after
        }else if (fieldName === 'standardOutput') {
            const obj = JSON.parse(before)
            beforeValue = parseDescription(obj.str)
            const obj2 = JSON.parse(after)
            afterValue = parseDescription(obj2.str)
        } else if (fieldName === 'reviewResult') {
            const reviewResultEnum = {
                null: t('reviewResultNull'),
                true: t('reviewResultTrue'),
                false: t('reviewResultFalse')
            }
            beforeValue = reviewResultEnum[before as keyof typeof reviewResultEnum] || before
            afterValue = reviewResultEnum[after as keyof typeof reviewResultEnum] || after
        }else if (fieldName === 'workGroup') {
            const workgroup = workgroupList.find(item => item.id === before.id)
            beforeValue = workgroup?.groupName || before
            const workgroup2 = workgroupList.find(item => item.id === after.id)
            afterValue = workgroup2?.groupName || after
        }
        
        return {
            label: fieldLabelMap[fieldName as keyof typeof fieldLabelMap] || fieldName,
            before: beforeValue,
            after: afterValue
        }
    }

    useEffect(() => {
        if (!dataObj?.changeFields) return

        try {
            // 解析变更字段
            const changes: IChangeField[] = JSON.parse(dataObj.changeFields)
            
            // 处理所有变更字段
            const processedChanges = changes.map(processField)
            
            // 生成对比文本
            const oldValue = processedChanges.map(change => 
                `${change.label}:\n${change.before}\n`
            ).join('')
            
            const newValue = processedChanges.map(change => 
                `${change.label}:\n${change.after}\n`
            ).join('')
            
            setDiffResult({
                oldValue,
                newValue
            })
        } catch (e) {
            console.error('Failed to parse change fields:', e)
            setDiffResult({
                oldValue: '',
                newValue: ''
            })
        }
    }, [dataObj])

    if (diffResult.oldValue && diffResult.newValue) {       
        return (
            <ReactDiffViewer
                oldValue={diffResult.oldValue}
                newValue={diffResult.newValue}
                splitView={true}
                disableWordDiff={true}
                leftTitle={t('before')}
                rightTitle={t('after')}
                styles={{ 
                    contentText: {
                        fontFamily: 'Helvetica Neue, Helvetica, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Arial, sans-serif',
                        color: 'rgba(0, 0, 0, 0.85)'
                    }
                }}
            />
        )
    }
    return <div>
        <p>{t('noData')}</p>
    </div>
}

export default DiffComp
